import React from 'react';
import { Badge } from '../../components/ui/badge';

export const CHARGE_LABELS = {
  fr: {
    transport_entrepot_aeroport: "Transport entrepôt → aéroport",
    main_oeuvre_preparation: "Main d'œuvre préparation",
    sac_emballage_plastique: "Sacs d'emballage + plastique film",
    scotch: "Scotch",
    tarif_kg_compagnie: "Tarif/kg compagnie",
    transport_aeroport_entrepot_yaounde: "Transport aéroport → entrepôt Yaoundé",
    livraison_colis_douala: "Livraison colis Douala",
    declaration_douane_france: "Déclaration douane France",
    dedouanement_cameroun: "Dédouanement Cameroun",
    autres_frais: "Autres frais (carburant, taxi)",
  },
  en: {
    transport_entrepot_aeroport: "Warehouse → airport transport",
    main_oeuvre_preparation: "Preparation labor",
    sac_emballage_plastique: "Packaging bags + plastic film",
    scotch: "Tape",
    tarif_kg_compagnie: "Airline rate/kg",
    transport_aeroport_entrepot_yaounde: "Airport → Yaoundé warehouse transport",
    livraison_colis_douala: "Douala package delivery",
    declaration_douane_france: "France customs declaration",
    dedouanement_cameroun: "Cameroon customs clearance",
    autres_frais: "Other costs (fuel, taxi)",
  },
};

export const SETTINGS_CHARGES = [
  'transport_entrepot_aeroport',
  'main_oeuvre_preparation',
  'sac_emballage_plastique',
  'scotch',
  'tarif_kg_compagnie',
  'transport_aeroport_entrepot_yaounde',
  'livraison_colis_douala',
  'autres_frais',
];

export const PAYMENT_STATUS_LABELS = {
  fr: {
    paid: { label: 'Payé', cls: 'bg-green-100 text-green-700 border-green-200' },
    advance: { label: 'Acompte', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    unpaid: { label: 'Non payé', cls: 'bg-red-100 text-red-700 border-red-200' },
  },
  en: {
    paid: { label: 'Paid', cls: 'bg-green-100 text-green-700 border-green-200' },
    advance: { label: 'Deposit', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    unpaid: { label: 'Unpaid', cls: 'bg-red-100 text-red-700 border-red-200' },
  },
};

export const fmt = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

export const fmtCurrency = (amount, currency) => {
  const symbols = { EUR: '€', USD: '$', XAF: ' FCFA', GBP: '£', CAD: 'CA$' };
  const sym = symbols[currency] || currency;
  return `${Number(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${sym}`;
};

export const KpiCard = ({ icon: Icon, label, value, sub, color = 'slate' }) => {
  const iconBgMap = {
    blue: { background: 'rgb(26 26 26 / 0.08)', color: '#1a1a1a' },
    orange: { background: 'rgb(232 89 12 / 0.1)', color: '#e8590c' },
    green: { background: 'rgb(16 185 129 / 0.1)', color: '#10b981' },
    red: { background: 'rgb(239 68 68 / 0.1)', color: '#ef4444' },
    amber: { background: 'rgb(245 158 11 / 0.1)', color: '#f59e0b' },
    slate: { background: 'rgb(102 102 102 / 0.1)', color: '#666666' },
  };
  const cardBgMap = {
    blue: { background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', borderColor: '#c7d2e8' },
    orange: { background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)', borderColor: '#f0c8a8' },
    green: { background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' },
    red: { background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)', borderColor: '#f0a8a8' },
    amber: { background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderColor: '#f0d88a' },
    slate: { background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', borderColor: '#cbd5e1' },
  };
  const iconStyle = iconBgMap[color] || iconBgMap.slate;
  const cardStyle = cardBgMap[color] || cardBgMap.slate;
  return (
    <div className="analytics-kpi" style={cardStyle}>
      <div className="analytics-kpi-header">
        <span className="analytics-kpi-label">{label}</span>
        <div className="analytics-kpi-icon" style={iconStyle}>
          <Icon size={18} />
        </div>
      </div>
      <div className="analytics-kpi-value">{value}</div>
      {sub && <div className="analytics-kpi-delta">{sub}</div>}
    </div>
  );
};

export const SummaryCard = ({ label, value, highlight, span2 }) => {
  const bgMap = {
    profit: 'bg-green-50 border-green-200 text-green-700',
    loss: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    ok: 'bg-muted border-border text-foreground',
  };
  const cls = highlight ? bgMap[highlight] : 'bg-muted border-border text-foreground';
  return (
    <div className={`border rounded-md p-3 ${cls} ${span2 ? 'col-span-2' : ''}`}>
      <div className="text-xs mb-1 opacity-70">{label}</div>
      <div className="font-semibold text-base">{value}</div>
    </div>
  );
};

export const TotalCard = ({ label, value, color = 'slate' }) => {
  const gradientMap = {
    green: { background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' },
    red: { background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)', borderColor: '#f0a8a8' },
    amber: { background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderColor: '#f0d88a' },
    slate: { background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', borderColor: '#cbd5e1' },
  };
  const textMap = {
    green: 'text-green-700',
    red: 'text-red-700',
    amber: 'text-amber-700',
    slate: 'text-foreground',
  };
  const cardStyle = gradientMap[color] || gradientMap.slate;
  return (
    <div className="border rounded-md p-3" style={cardStyle}>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`font-bold text-base ${textMap[color]}`}>{value}</div>
    </div>
  );
};

export const PaymentProgressBar = ({ paid, total, currency }) => {
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[100px] sm:min-w-[140px]">
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-amber-500' : 'bg-red-300'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-medium text-muted-foreground w-9 text-right">{pct}%</span>
    </div>
  );
};

export const getInvoiceStatus = (inv) => {
  if (inv.is_settled) return 'paid';
  const age = inv.created_at ? Math.floor((Date.now() - new Date(inv.created_at).getTime()) / 86400000) : 0;
  if (age > 30) return 'overdue';
  return 'pending';
};

export const InvoiceStatusBadge = ({ status, t }) => {
  const cfg = {
    paid: { label: t('paidStatus'), cls: 'bg-green-100 text-green-700 border-green-200' },
    pending: { label: t('pendingFilter'), cls: 'bg-red-100 text-red-700 border-red-200' },
    overdue: { label: t('overdueStatus'), cls: 'bg-red-100 text-red-700 border-red-200' },
  };
  const c = cfg[status] || cfg.pending;
  return <Badge variant="outline" className={`text-[11px] ${c.cls}`}>{c.label}</Badge>;
};
